import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdAssessment, MdEdit, MdFileDownload, MdPrint, MdSave } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import { PAYMENTS_UPDATE_EVENT } from '../receptionist/paymentStore';
import { fetchZReportActiveMemberStats } from '../../services/receptionist/receptionistApi';
import {
  fetchDailyZReport1,
  saveDailyZReport1,
} from '../../services/admin/financeLedgerApi';
import { fetchZReportTotalsRpc } from '../../services/admin/zReportRpcApi';
import { getCachedGymId, ensureGymId } from '../../services/receptionist/gymContext';
import { isReceptionistPanelScope } from '../../services/gym/branchContext';
import { pkTodayKey } from '../../utils/pkTime';
import { loadAdminSettings } from '../admin/settings/settingsData';
import { localDateKey } from '../admin/finance/financeData';
import {
  printDailyZReport1,
  exportDailyZReport1Csv,
  exportDailyZReport1Excel,
  exportDailyZReport1Pdf,
} from './DailyZReport1Export';
import {
  Z1_BANK_ACCOUNTS,
  Z1_SALE_ITEMS,
  Z1_PAYMENT_ITEMS,
  Z1_ACTIVE_ITEMS,
  emptyZReport1Manual,
  cloneZReport1Manual,
  remoteToZReport1Manual,
  computeZ1Closings,
  applyTodayAutoToManual,
  applyActiveMemberSeed,
  applyOpeningSeedFromPrevious,
  buildZReport1SavePayload,
  sumTodaySale,
  sumTodaySaleCount,
  sumMonthSale,
  sumTodayPayment,
  sumTodayPaymentCount,
  sumMonthPayment,
  sumExpenseGym,
  sumExpensePearl,
} from './zReport1Data';
import '../admin/finance/finance.css';
import './DailyZReport.css';
import './DailyZReport1.css';

const z1TodayAutoCache = new Map();

export function clearZ1TodayAutoCache() {
  z1TodayAutoCache.clear();
}

function money(value) {
  const n = Number(value) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const d = new Date(`${day}T12:00:00`);
  if (Number.isNaN(d.getTime())) return day;
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
}

function previousReportDate(isoDay) {
  const day = String(isoDay || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return '';
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return localDateKey(d);
}

function ManualInput({ value, onChange, ariaLabel, readOnly = false }) {
  if (readOnly) {
    return (
      <span className="dz-manual-value" aria-label={ariaLabel}>
        {value === '' || value == null ? '0' : money(value)}
      </span>
    );
  }
  return (
    <input
      type="number"
      min="0"
      step="1"
      className="dz-manual-input dz1-manual-input"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      placeholder="0"
    />
  );
}

/**
 * Excel-layout Z Report 1 — separate from classic DailyZReport.
 */
export default function DailyZReport1({
  title = 'Z Report 1',
  subtitle = 'Bank (3 accounts) + Cash summary, Today auto sale/payment, rest manual',
  embedded = false,
  initialDate = '',
} = {}) {
  const toast = usePanelToast();
  const receptionistScope = isReceptionistPanelScope();
  const todayPk = pkTodayKey();
  const [reportDate, setReportDate] = useState(() => (
    isReceptionistPanelScope()
      ? pkTodayKey()
      : (initialDate || localDateKey())
  ));
  const [gymId, setGymId] = useState(() => getCachedGymId() || '');
  const [manual, setManual] = useState(() => emptyZReport1Manual());
  const [todayAuto, setTodayAuto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(true);
  const [hasSavedReport, setHasSavedReport] = useState(false);
  const draftRef = useRef(null);
  const loadSeqRef = useRef(0);
  const hasPaintedRef = useRef(false);
  const isEditingRef = useRef(true);

  const gymTitle = useMemo(() => {
    const settings = loadAdminSettings(gymId);
    const name = settings?.gym?.name?.trim() || settings?.softwareName?.trim() || 'OXYGYM HEALTH & FITNESS CLUB';
    const branch = settings?.gym?.branch?.trim() || settings?.gym?.address?.trim() || '';
    return { name: name.toUpperCase(), branch: branch.toUpperCase() };
  }, [gymId]);

  const closings = useMemo(
    () => computeZ1Closings(manual, todayAuto?.cashSale),
    [manual, todayAuto],
  );

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (receptionistScope && reportDate !== todayPk) {
      setReportDate(todayPk);
    }
  }, [receptionistScope, reportDate, todayPk]);

  /** Cached single-day RPC — same pattern as classic Z Report range cache. */
  const fetchTodayAutoCached = useCallback(async (day, gid, { bust = false } = {}) => {
    const key = `${gid || ''}|${day}`;
    if (!bust && z1TodayAutoCache.has(key)) return z1TodayAutoCache.get(key);
    const pending = fetchZReportTotalsRpc(day, day, gid).catch(() => null);
    z1TodayAutoCache.set(key, pending);
    try {
      return await pending;
    } catch (err) {
      z1TodayAutoCache.delete(key);
      throw err;
    }
  }, []);

  const loadData = useCallback(async ({ preserveEdit = false, bustAuto = false } = {}) => {
    const seq = ++loadSeqRef.current;
    try {
      setError('');
      // First open: full loading. Later date/refresh: keep sheet visible (SWR).
      if (!preserveEdit && !hasPaintedRef.current) setLoading(true);
      else if (!preserveEdit) setAutoRefreshing(true);

      const gid = getCachedGymId() || await ensureGymId().catch(() => '');
      if (gid) setGymId(gid);
      const prevDay = previousReportDate(reportDate);

      // Critical path only — same as classic Z Report (member stats are background)
      const [remote, autoRpc, prevRemote] = await Promise.all([
        fetchDailyZReport1(reportDate, gid).catch((err) => {
          if (/z_report/i.test(err.message || '')) setError(err.message);
          return null;
        }),
        fetchTodayAutoCached(reportDate, gid, { bust: bustAuto }).catch(() => null),
        prevDay && gid
          ? fetchDailyZReport1(prevDay, gid).catch(() => null)
          : Promise.resolve(null),
      ]);

      if (seq !== loadSeqRef.current) return;
      setTodayAuto(autoRpc);

      const saved = Boolean(remote?.hasV1);
      setHasSavedReport(saved);

      let nextManual = saved
        ? remoteToZReport1Manual(remote.zReportV1)
        : emptyZReport1Manual();

      if (!saved && prevRemote?.zReportV1) {
        nextManual = applyOpeningSeedFromPrevious(nextManual, prevRemote.zReportV1);
      }

      // Auto-fill Today sale/payment only into blank cells.
      // Saved reports: still fill blanks (e.g. new day fields), never wipe typed values.
      nextManual = applyTodayAutoToManual(nextManual, autoRpc);

      if (!preserveEdit) {
        setManual(nextManual);
        draftRef.current = cloneZReport1Manual(nextManual);
        setIsEditing(!saved);
      } else if (!isEditingRef.current && saved) {
        const viewManual = applyTodayAutoToManual(
          remoteToZReport1Manual(remote.zReportV1),
          autoRpc,
        );
        setManual(viewManual);
        draftRef.current = cloneZReport1Manual(viewManual);
      }

      hasPaintedRef.current = true;
    } catch (err) {
      if (seq === loadSeqRef.current) {
        setError(err.message || 'Could not load Z Report 1');
      }
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
        setAutoRefreshing(false);
      }
    }

    // Background — does not block the sheet (classic Z Report pattern)
    fetchZReportActiveMemberStats(reportDate)
      .then((stats) => {
        if (seq !== loadSeqRef.current || !stats) return;
        setManual((prev) => {
          const seeded = applyActiveMemberSeed(prev, stats);
          draftRef.current = cloneZReport1Manual(seeded);
          return seeded;
        });
      })
      .catch(() => {});
  }, [reportDate, fetchTodayAutoCached]);

  useEffect(() => {
    loadData({ preserveEdit: false });
  }, [loadData]);

  useEffect(() => {
    const onPayments = () => {
      clearZ1TodayAutoCache();
      loadData({ preserveEdit: true, bustAuto: true });
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, onPayments);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, onPayments);
  }, [loadData]);

  const setBank = (account, field, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      bankAccounts: {
        ...prev.bankAccounts,
        [account]: { ...prev.bankAccounts[account], [field]: value },
      },
    }));
  };

  const setCash = (field, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      cashSummary: { ...prev.cashSummary, [field]: value },
    }));
  };

  const setTodaySale = (id, field, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      todaySaleSummary: {
        ...prev.todaySaleSummary,
        [id]: { ...prev.todaySaleSummary[id], [field]: value },
      },
    }));
  };

  const setMonthSale = (id, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      monthSaleSummary: { ...prev.monthSaleSummary, [id]: value },
    }));
  };

  const setTodayPay = (id, field, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      todayPaymentMethod: {
        ...prev.todayPaymentMethod,
        [id]: { ...prev.todayPaymentMethod[id], [field]: value },
      },
    }));
  };

  const setMonthPay = (id, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      monthPaymentMethod: { ...prev.monthPaymentMethod, [id]: value },
    }));
  };

  const setActive = (id, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      totalActiveMembers: { ...prev.totalActiveMembers, [id]: value },
    }));
  };

  const setExpense = (field, value) => {
    if (!isEditing) return;
    setManual((prev) => ({
      ...prev,
      totalExpenseSummary: { ...prev.totalExpenseSummary, [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const gid = gymId || getCachedGymId() || await ensureGymId();
      const payload = buildZReport1SavePayload(manual, closings);
      const savedRow = await saveDailyZReport1({
        reportDate,
        zReportV1: payload,
      }, gid);

      loadSeqRef.current += 1;
      // Prefer server snapshot so refresh/next-day load matches what was persisted
      const snapshot = savedRow?.zReportV1
        ? remoteToZReport1Manual(savedRow.zReportV1)
        : cloneZReport1Manual(manual);
      setManual(snapshot);
      draftRef.current = cloneZReport1Manual(snapshot);
      setHasSavedReport(true);
      setIsEditing(false);
      toast.showSaved('Z Report 1');
    } catch (err) {
      toast.showError(err.message || 'Could not save Z Report 1', 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = () => {
    draftRef.current = cloneZReport1Manual(manual);
    setIsEditing(true);
    toast.showSuccess(
      `Editing Z Report 1 for ${formatReportDate(reportDate)}. Change fields, then Save.`,
      'Edit mode',
    );
  };

  const handleCancelEdit = () => {
    if (draftRef.current) setManual(cloneZReport1Manual(draftRef.current));
    setIsEditing(false);
    toast.showSuccess('Changes discarded. Showing saved Z Report 1.', 'View mode');
  };

  const buildExportPayload = () => ({
    gymTitle,
    reportDate,
    manual,
    autoTodayCashSale: todayAuto?.cashSale,
  });

  const handlePrint = () => {
    const ok = printDailyZReport1(buildExportPayload());
    if (!ok) toast.showError('Allow pop-ups to print the Z Report 1.', 'Print blocked');
  };

  const handleExportCsv = () => {
    exportDailyZReport1Csv(buildExportPayload());
    toast.showSuccess('CSV download started.', 'Exported');
  };

  const handleExportExcel = () => {
    exportDailyZReport1Excel(buildExportPayload());
    toast.showSuccess('Excel download started.', 'Exported');
  };

  const handleExportPdf = () => {
    try {
      const ok = exportDailyZReport1Pdf(buildExportPayload());
      if (ok === false) {
        toast.showError('Could not create PDF file.', 'Export failed');
        return;
      }
      toast.showSuccess('PDF download started.', 'Exported');
    } catch (err) {
      toast.showError(err.message || 'Could not export PDF', 'Export failed');
    }
  };

  const gymExpenseTotal = sumExpenseGym(manual);
  const pearlExpenseTotal = sumExpensePearl(manual);

  const toolbarActions = (
    <div className="report-toolbar-actions dz-toolbar-actions dz-toolbar-row">
      <div className="dz-toolbar-date">
        <label className="owner-form-label" htmlFor={embedded ? 'dz1-date-embedded' : 'dz1-date'}>Date</label>
        <input
          id={embedded ? 'dz1-date-embedded' : 'dz1-date'}
          type="date"
          className="owner-form-input"
          value={receptionistScope ? todayPk : reportDate}
          max={receptionistScope ? todayPk : undefined}
          readOnly={receptionistScope}
          disabled={receptionistScope}
          onChange={(e) => {
            if (receptionistScope) return;
            setReportDate(e.target.value || localDateKey());
          }}
        />
      </div>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handlePrint}>
        <MdPrint aria-hidden /> Print
      </button>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleExportCsv}>
        <MdFileDownload aria-hidden /> Export CSV
      </button>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleExportExcel}>
        <MdFileDownload aria-hidden /> Export Excel
      </button>
      <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleExportPdf}>
        <MdFileDownload aria-hidden /> Export PDF
      </button>
      {isEditing && hasSavedReport ? (
        <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleCancelEdit}>
          Cancel
        </button>
      ) : null}
      {isEditing ? (
        <button type="button" className="owner-btn owner-btn-primary owner-btn-sm dz-toolbar-save" onClick={handleSave} disabled={saving}>
          <MdSave aria-hidden /> {saving ? 'Saving…' : (hasSavedReport ? 'Update Z Report 1' : 'Save Z Report 1')}
        </button>
      ) : (
        <button type="button" className="owner-btn owner-btn-primary owner-btn-sm dz-toolbar-save" onClick={handleEdit}>
          <MdEdit aria-hidden /> Edit
        </button>
      )}
    </div>
  );

  const statusBadge = hasSavedReport && !isEditing
    ? <span className="owner-badge owner-badge-info">Saved — view mode</span>
    : (isEditing && hasSavedReport
      ? <span className="owner-badge owner-badge-warning">Editing</span>
      : (!hasSavedReport && isEditing
        ? <span className="owner-badge owner-badge-info">New report</span>
        : null));

  const sheet = (
    <div className={`owner-card rec-panel-card admin-finance-section dz-sheet dz1-sheet${isEditing ? '' : ' is-view-only'}${loading ? ' is-loading' : ''}`} id="daily-z-report-1-print">
      <div className="dz-header admin-z-sale-header">
        <div className="dz-brand admin-z-sale-brand dz1-brand">{gymTitle.name}</div>
        {gymTitle.branch ? <div className="dz-branch dz1-branch">{gymTitle.branch}</div> : null}
        <div className="dz-title-row admin-z-sale-subtitle-row dz1-title-row">
          <span className="dz-title admin-z-sale-subtitle">Z-REPORT</span>
          <span className="dz-date admin-z-sale-date">DATE : {formatReportDate(reportDate)}</span>
        </div>
        <p className="dz-showing-date no-print">
          Showing Z Report 1 for <strong>{formatReportDate(reportDate)}</strong>
          {hasSavedReport ? ' (saved)' : ' (not saved yet)'}
          {isEditing ? ' · editable' : ' · view only'}
          {autoRefreshing ? ' · updating today totals…' : null}
        </p>
      </div>

      <div className="dz1-table-wrap">
        <table className="dz-table dz1-table dz1-bank-cash-table">
          <colgroup>
            <col className="dz1-item-col" />
            {Z1_BANK_ACCOUNTS.map((acc) => <col key={acc} className="dz1-bank-col" />)}
            <col className="dz1-cash-item-col" />
            <col className="dz1-cash-amt-col" />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={4}>Bank Summery</th>
              <th colSpan={2}>Cash Summery</th>
            </tr>
            <tr>
              <th>Item</th>
              {Z1_BANK_ACCOUNTS.map((acc) => <th key={acc}>{acc}</th>)}
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Opening Balance</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.bankAccounts[acc].opening} onChange={(v) => setBank(acc, 'opening', v)} ariaLabel={`${acc} opening`} />
                </td>
              ))}
              <td>Opening Balance</td>
              <td className="dz-num">
                <ManualInput readOnly={!isEditing} value={manual.cashSummary.opening} onChange={(v) => setCash('opening', v)} ariaLabel="Cash opening" />
              </td>
            </tr>
            <tr>
              <td>Bank Received</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.bankAccounts[acc].received} onChange={(v) => setBank(acc, 'received', v)} ariaLabel={`${acc} received`} />
                </td>
              ))}
              <td>Cash Received</td>
              <td className="dz-num">
                <ManualInput readOnly={!isEditing} value={manual.cashSummary.received} onChange={(v) => setCash('received', v)} ariaLabel="Cash received" />
              </td>
            </tr>
            <tr>
              <td>Bank Sale</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.bankAccounts[acc].sale} onChange={(v) => setBank(acc, 'sale', v)} ariaLabel={`${acc} sale`} />
                </td>
              ))}
              <td>
                Today Cash Sale
                <span className="dz1-auto-hint"> (auto)</span>
              </td>
              <td className="dz-num">
                {isEditing ? (
                  <ManualInput
                    readOnly={false}
                    value={manual.cashSummary.todayCashSale}
                    onChange={(v) => setCash('todayCashSale', v)}
                    ariaLabel="Today cash sale"
                  />
                ) : (
                  <strong>{money(closings.cashSaleUsed)}</strong>
                )}
              </td>
            </tr>
            <tr>
              <td>Pearl Group Expenses</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.bankAccounts[acc].pearlGroupExpenses} onChange={(v) => setBank(acc, 'pearlGroupExpenses', v)} ariaLabel={`${acc} pearl expenses`} />
                </td>
              ))}
              <td>Pearl Group Expenses</td>
              <td className="dz-num">
                <ManualInput readOnly={!isEditing} value={manual.cashSummary.pearlGroupExpenses} onChange={(v) => setCash('pearlGroupExpenses', v)} ariaLabel="Cash pearl expenses" />
              </td>
            </tr>
            <tr>
              <td>Gym Expenses</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.bankAccounts[acc].gymExpenses} onChange={(v) => setBank(acc, 'gymExpenses', v)} ariaLabel={`${acc} gym expenses`} />
                </td>
              ))}
              <td>Gym Expenses</td>
              <td className="dz-num">
                <ManualInput readOnly={!isEditing} value={manual.cashSummary.gymExpenses} onChange={(v) => setCash('gymExpenses', v)} ariaLabel="Cash gym expenses" />
              </td>
            </tr>
            <tr className="dz-total">
              <td>Closing Balance</td>
              {Z1_BANK_ACCOUNTS.map((acc) => (
                <td key={acc} className="dz-num"><strong>{money(closings.closingBanks[acc])}</strong></td>
              ))}
              <td>Closing Balance</td>
              <td className="dz-num"><strong>{money(closings.closingCash)}</strong></td>
            </tr>
          </tbody>
        </table>

        <table className="dz-table dz1-table">
          <thead>
            <tr>
              <th colSpan={3}>Today Sale Summery</th>
              <th colSpan={2}>During Month Sale Summery</th>
            </tr>
            <tr>
              <th>Item</th>
              <th>No</th>
              <th>Amount</th>
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {Z1_SALE_ITEMS.map((row) => (
              <tr key={row.id}>
                <td>{row.label}</td>
                <td className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.todaySaleSummary[row.id].count}
                    onChange={(v) => setTodaySale(row.id, 'count', v)}
                    ariaLabel={`Today ${row.label} count`}
                  />
                </td>
                <td className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.todaySaleSummary[row.id].amount}
                    onChange={(v) => setTodaySale(row.id, 'amount', v)}
                    ariaLabel={`Today ${row.label} amount`}
                  />
                </td>
                <td>{row.label}</td>
                <td className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.monthSaleSummary[row.id]}
                    onChange={(v) => setMonthSale(row.id, v)}
                    ariaLabel={`Month ${row.label}`}
                  />
                </td>
              </tr>
            ))}
            <tr className="dz-total">
              <td>Total Sale</td>
              <td className="dz-num"><strong>{sumTodaySaleCount(manual)}</strong></td>
              <td className="dz-num"><strong>{money(sumTodaySale(manual))}</strong></td>
              <td>Total Sale</td>
              <td className="dz-num"><strong>{money(sumMonthSale(manual))}</strong></td>
            </tr>
          </tbody>
        </table>

        <table className="dz-table dz1-table">
          <thead>
            <tr>
              <th colSpan={3}>Today Payment Method</th>
              <th colSpan={2}>During Month Payment Method</th>
            </tr>
            <tr>
              <th>Item</th>
              <th>No</th>
              <th>Amount</th>
              <th>Item</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {Z1_PAYMENT_ITEMS.map((row) => (
              <tr key={row.id}>
                <td>{row.label}</td>
                <td className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.todayPaymentMethod[row.id].count}
                    onChange={(v) => setTodayPay(row.id, 'count', v)}
                    ariaLabel={`Today ${row.label} count`}
                  />
                </td>
                <td className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.todayPaymentMethod[row.id].amount}
                    onChange={(v) => setTodayPay(row.id, 'amount', v)}
                    ariaLabel={`Today ${row.label} amount`}
                  />
                </td>
                <td>{row.label}</td>
                <td className="dz-num">
                  <ManualInput
                    readOnly={!isEditing}
                    value={manual.monthPaymentMethod[row.id]}
                    onChange={(v) => setMonthPay(row.id, v)}
                    ariaLabel={`Month ${row.label}`}
                  />
                </td>
              </tr>
            ))}
            <tr className="dz-total">
              <td>Total Received</td>
              <td className="dz-num"><strong>{sumTodayPaymentCount(manual)}</strong></td>
              <td className="dz-num"><strong>{money(sumTodayPayment(manual))}</strong></td>
              <td>Total Received</td>
              <td className="dz-num"><strong>{money(sumMonthPayment(manual))}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className="dz1-grid-2">
          <table className="dz-table dz1-table">
            <thead>
              <tr><th colSpan={2}>Total Active Members</th></tr>
              <tr><th>Item</th><th>Amount</th></tr>
            </thead>
            <tbody>
              {Z1_ACTIVE_ITEMS.map((row) => (
                <tr key={row.id}>
                  <td>{row.label}</td>
                  <td className="dz-num">
                    <ManualInput
                      readOnly={!isEditing}
                      value={manual.totalActiveMembers[row.id]}
                      onChange={(v) => setActive(row.id, v)}
                      ariaLabel={row.label}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="dz-table dz1-table">
            <thead>
              <tr><th colSpan={2}>Total Expense Summery</th></tr>
              <tr><th>Item</th><th>Amount</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Gym Cash Expense</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.gym_cash} onChange={(v) => setExpense('gym_cash', v)} ariaLabel="Gym cash expense" />
                </td>
              </tr>
              <tr>
                <td>Gym Bank Expense</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.gym_bank} onChange={(v) => setExpense('gym_bank', v)} ariaLabel="Gym bank expense" />
                </td>
              </tr>
              <tr className="dz-subtotal">
                <td>Total</td>
                <td className="dz-num"><strong>{money(gymExpenseTotal)}</strong></td>
              </tr>
              <tr>
                <td>Pearl Cash Expense</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.pearl_cash} onChange={(v) => setExpense('pearl_cash', v)} ariaLabel="Pearl cash expense" />
                </td>
              </tr>
              <tr>
                <td>Pearl Bank Expense</td>
                <td className="dz-num">
                  <ManualInput readOnly={!isEditing} value={manual.totalExpenseSummary.pearl_bank} onChange={(v) => setExpense('pearl_bank', v)} ariaLabel="Pearl bank expense" />
                </td>
              </tr>
              <tr className="dz-subtotal">
                <td>Total</td>
                <td className="dz-num"><strong>{money(pearlExpenseTotal)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="dz1-page dz-page">
        <div className="owner-card rec-panel-card admin-finance-section dz-toolbar no-print">
          <div className="dz-toolbar-head">
            <h2 className="owner-card-title">{title}</h2>
            {statusBadge}
          </div>
          {toolbarActions}
          {error ? <p className="owner-placeholder-text" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p> : null}
          {loading ? <p className="owner-placeholder-text">Loading Z Report 1…</p> : null}
        </div>
        {sheet}
      </div>
    );
  }

  return (
    <div className="dz1-page dz-page">
      <PageHeader title={title} subtitle={subtitle} variant="banner">
        <span className="owner-badge owner-badge-info" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <MdAssessment aria-hidden />
          Daily close
        </span>
      </PageHeader>

      {error ? (
        <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>
          {error}
        </p>
      ) : null}
      {loading ? <p className="owner-placeholder-text owner-mb-16">Loading Z Report 1…</p> : null}

      <div className="owner-card rec-panel-card admin-finance-section dz-toolbar no-print">
        <div className="dz-toolbar-head">
          <h2 className="owner-card-title">Report date</h2>
          {statusBadge}
        </div>
        {toolbarActions}
      </div>

      {sheet}
    </div>
  );
}
